import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Check, Info, Lock } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import PublicSiteLayout from "@/components/landing/PublicSiteLayout";
import StepIndicator from "@/components/StepIndicator";
import { useTranslation } from "react-i18next";

const plans = [
  {
    id: "starter",
    price: 0,
    period: "/mo",
    popular: false,
  },
  {
    id: "pro",
    price: 49,
    period: "/mo",
    popular: true,
  },
  {
    id: "team",
    price: 64,
    period: "/mo",
    popular: false,
  },
];

export default function Pricing() {
  const { t } = useTranslation();
  const [annual, setAnnual] = useState(true);
  const navigate = useNavigate();

  const choosePlan = (planId: string) => {
    const interval = planId === "starter" ? "MONTHLY" : annual ? "YEARLY" : "MONTHLY";
    navigate(`/create-account?plan=${planId}&interval=${interval}`);
  };

  return (
    <PublicSiteLayout>
      <div className="min-h-screen bg-background">
        <StepIndicator currentStep={1} />
        <div className="container mx-auto px-4 pb-20 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <h1 className="text-3xl font-bold text-primary md:text-4xl">{t("pricing.title")}</h1>
            <p className="mt-3 text-muted-foreground">{t("pricing.subtitle")}</p>
          </div>

          <div className="mt-8 flex items-center justify-center gap-3">
            <span className={`text-sm font-medium ${!annual ? "text-foreground" : "text-muted-foreground"}`}>{t("pricing.monthly")}</span>
            <button
              type="button"
              onClick={() => setAnnual((prev) => !prev)}
              className={`relative h-6 w-11 rounded-full transition-colors ${annual ? "bg-accent" : "bg-border"}`}
              aria-label={t("pages.upgradePlan.billingCycle")}
            >
              <div
                className={`absolute top-0.5 h-5 w-5 rounded-full bg-card shadow transition-transform ${
                  annual ? "left-0.5 translate-x-5" : "left-0.5"
                }`}
              />
            </button>
            <span className={`text-sm font-medium ${annual ? "text-foreground" : "text-muted-foreground"}`}>
              {t("pricing.annual")} <Badge variant="secondary" className="ml-1 text-xs">{t("pricing.save20")}</Badge>
            </span>
          </div>

          <div className="mx-auto mt-6 max-w-5xl rounded-lg border border-accent/30 bg-accent/10 px-4 py-3 sm:px-5">
            <div className="flex items-start gap-2.5">
              <Lock className="mt-0.5 h-4 w-4 shrink-0 text-accent" />
              <div>
                <p className="text-sm font-semibold text-foreground">{t("pricing.foundersBannerTitle")}</p>
                <p className="text-sm text-muted-foreground">{t("pricing.foundersBannerBody")}</p>
              </div>
            </div>
          </div>
          <div className="mx-auto mt-12 grid max-w-5xl gap-6 md:grid-cols-3">
            {plans.map((plan) => {
              const displayPrice = annual ? Math.round(plan.price * 0.8) : plan.price;
              const annualTotal = displayPrice * 12;
              const featureKeys = [
                `pricing.cards.${plan.id}.features.0`,
                `pricing.cards.${plan.id}.features.1`,
                `pricing.cards.${plan.id}.features.2`,
                `pricing.cards.${plan.id}.features.3`,
                `pricing.cards.${plan.id}.features.4`,
              ].filter((key) => t(key) !== key);
              const showFoundersBadge = plan.id === "pro" || plan.id === "team";
              const showLockedFeature = plan.id === "pro" || plan.id === "team";

              return (
                <div
                  key={plan.id}
                  className={`relative rounded-xl border p-6 transition-all ${
                    plan.popular
                      ? "border-accent bg-card shadow-md ring-1 ring-accent/25"
                      : "border-border bg-card shadow-sm hover:shadow-md"
                  }`}
                >
                  {plan.popular && (
                    <Badge className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-accent text-accent-foreground">
                      {t("pricing.mostPopular")}
                    </Badge>
                  )}
                  {showFoundersBadge && (
                    <Badge
                      variant="outline"
                      className="absolute right-4 top-4 inline-flex items-center gap-1 whitespace-nowrap border-accent/70 text-accent"
                    >
                      <Lock className="h-3 w-3" />
                      {t("pricing.foundersBadge")}
                    </Badge>
                  )}
                  <h3 className="text-xl font-bold text-primary">
                    {t(`pricing.cards.${plan.id}.name`)}
                    {plan.id === "starter" ? ` (${t("pricing.starterTrial")})` : ""}
                  </h3>
                  <p className="mt-1 text-sm text-muted-foreground">{t(`pricing.cards.${plan.id}.desc`)}</p>
                  <div className="mt-4">
                    <span className="text-4xl font-extrabold text-card-foreground">${displayPrice}</span>
                    <span className="text-muted-foreground">{plan.period}</span>
                    <div
                      className={`overflow-hidden transition-all duration-300 ease-out ${
                        annual && plan.price > 0 ? "mt-1 max-h-8 opacity-100" : "max-h-0 opacity-0"
                      }`}
                    >
                      <p className="text-xs font-medium text-accent">
                        {t("pricing.billedForOneYear", { amount: `$${annualTotal}` })}
                      </p>
                    </div>
                  </div>
                  <Button
                    className={`mt-6 w-full ${plan.popular ? "bg-accent text-accent-foreground hover:bg-accent/90" : ""}`}
                    variant={plan.popular ? "default" : "outline"}
                    onClick={() => choosePlan(plan.id)}
                  >
                    {plan.price === 0 ? t("pricing.startFree") : t("pricing.choosePlan")}
                  </Button>
                  <ul className="mt-6 space-y-2.5">
                    {featureKeys.map((featureKey) => (
                      <li key={featureKey} className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Check className="h-4 w-4 text-accent" />
                        {t(featureKey)}
                      </li>
                    ))}
                    {showLockedFeature ? (
                      <li className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Check className="h-4 w-4 text-accent" />
                        <span>{t("pricing.lockedFeature")}</span>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button
                              type="button"
                              className="inline-flex h-4 w-4 items-center justify-center rounded-full text-accent/90 transition-colors hover:text-accent"
                              aria-label={t("pricing.lockedFeatureTooltipAria")}
                            >
                              <Info className="h-3.5 w-3.5" />
                            </button>
                          </TooltipTrigger>
                          <TooltipContent className="max-w-[260px] text-xs leading-5">
                            <p>{t("pricing.lockedTooltipLine1")}</p>
                            <p>{t("pricing.lockedTooltipLine2")}</p>
                          </TooltipContent>
                        </Tooltip>
                      </li>
                    ) : null}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </PublicSiteLayout>
  );
}
